import React, { useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Upload, FileText, CheckCircle, Trash2, Loader2, AlertCircle, RefreshCw, X } from 'lucide-react';
import { useDocumentStats } from '../hooks';
import { agentApi } from '../api';
import type { AgentDocument } from '../api/agent';
import { usersApi, UserListItem } from '../api/users';
import { formatRelativeDate } from '../utils/date';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'ready':
    case 'indexed':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
          <CheckCircle size={12} /> Indexed
        </span>
      );
    case 'processing':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-100">
          <Loader2 size={12} className="animate-spin" /> Processing
        </span>
      );
    case 'error':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">
          <AlertCircle size={12} /> Error
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-50 text-slate-700 border border-slate-100">
          {status}
        </span>
      );
  }
};

const Documents: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId') || '';
  const pageFromUrl = searchParams.get('page');

  const paramsRef = React.useRef<Record<string, string>>({});
  paramsRef.current = Object.fromEntries(searchParams.entries());

  const updateUrlParams = (updates: Record<string, string | null>) => {
    const merged = { ...paramsRef.current, ...updates };
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(merged)) {
      if (v) params[k] = v;
    }
    paramsRef.current = params;
    setSearchParams(params, { replace: true });
  };

  const [page, setPageState] = useState(pageFromUrl ? parseInt(pageFromUrl, 10) : 1);
  const setPage = (p: number) => {
    setPageState(p);
    updateUrlParams({ page: p > 1 ? String(p) : null });
  };
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useDocumentStats();

  // Documents from agent-server directly (per-user)
  const [documents, setDocuments] = useState<AgentDocument[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [docsLoading, setDocsLoading] = useState(false);

  // Users from agent-server directly
  const [users, setUsers] = useState<UserListItem[]>([]);

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedUserId, setSelectedUserIdState] = useState<string>(userIdFromUrl);
  const setSelectedUserId = (userId: string) => {
    setSelectedUserIdState(userId);
    updateUrlParams({ userId: userId || null, page: null });
  };
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const totalPages = Math.ceil(totalDocs / 20);

  // Load users list on mount
  useState(() => {
    usersApi
      .list({ limit: 100 })
      .then((res) => setUsers(res.users))
      .catch(console.error);
  });

  // Load documents when user or page changes
  const fetchDocuments = async () => {
    if (!selectedUserId) {
      setDocuments([]);
      setTotalDocs(0);
      return;
    }
    setDocsLoading(true);
    try {
      const offset = (page - 1) * 20;
      const response = await agentApi.listDocuments(selectedUserId, { limit: 20, offset });
      setDocuments(response.documents);
      setTotalDocs(response.pagination.total);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setDocuments([]);
      setTotalDocs(0);
    } finally {
      setDocsLoading(false);
    }
  };

  const refetchDocs = () => fetchDocuments();

  // Reload documents when user or page changes
  React.useEffect(() => {
    fetchDocuments();
  }, [selectedUserId, page]);

  const isLoading = docsLoading || statsLoading;

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !selectedUserId) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(`Uploading ${file.name}... (${i + 1}/${files.length})`);
        await agentApi.uploadDocument(selectedUserId, file);
      }
      setShowUploadModal(false);
      setUploadProgress(null);
      refetchDocs();
      refetchStats();
    } catch (err) {
      console.error('File upload error:', err);
      setUploadError(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (docId: string) => {
    if (!selectedUserId) return;
    setDeletingId(docId);
    try {
      await agentApi.deleteDocument(selectedUserId, docId);
      setShowDeleteConfirm(null);
      refetchDocs();
      refetchStats();
    } catch (err) {
      console.error('Delete error:', err);
      alert(`Failed to delete document: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!selectedUserId) {
        setUploadError('Please select a user first');
        return;
      }

      const files = event.dataTransfer.files;
      if (files.length === 0) return;

      setIsUploading(true);
      setUploadError(null);

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setUploadProgress(`Uploading ${file.name}... (${i + 1}/${files.length})`);
          await agentApi.uploadDocument(selectedUserId, file);
        }
        setShowUploadModal(false);
        setUploadProgress(null);
        refetchDocs();
        refetchStats();
      } catch (err) {
        console.error('File upload error:', err);
        setUploadError(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
      }
    },
    [selectedUserId, refetchDocs, refetchStats],
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Calculate storage usage percentage (assuming 10GB limit)
  const maxStorage = 10 * 1024 * 1024 * 1024; // 10GB
  const usedStorage = stats?.total_storage_bytes || 0;
  const usagePercent = Math.min(Math.round((usedStorage / maxStorage) * 100), 100);
  const strokeOffset = 440 - (440 * usagePercent) / 100;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Knowledge Base</h2>
        <div className="flex gap-2 items-center">
          <select
            value={selectedUserId}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setPageState(1);
            }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="">Select User...</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.user?.name || user.user_id} ({user.tier?.id || 'free'})
              </option>
            ))}
          </select>
          <button
            onClick={() => refetchDocs()}
            className="bg-slate-100 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
          >
            <Upload size={16} />
            Upload Document
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 size={32} className="animate-spin mx-auto mb-4 text-blue-500" />
            <p className="text-slate-500">Loading documents...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <h3 className="font-semibold text-slate-800">Uploaded Documents ({totalDocs})</h3>
            </div>
            {documents.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-slate-400">
                <div className="text-center">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No documents uploaded yet</p>
                  <p className="text-sm mt-1">Upload documents to start building your knowledge base</p>
                </div>
              </div>
            ) : (
              <>
                <table className="w-full text-left">
                  <thead className="bg-white border-b border-slate-100">
                    <tr>
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {documents.map((doc: AgentDocument) => {
                      const docId = String(doc.id);
                      return (
                        <tr key={docId} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                <FileText size={18} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-700">{doc.file.name}</p>
                                <p className="text-xs text-slate-400">
                                  {formatFileSize(doc.file.size)} • {formatRelativeDate(doc.created_at)}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">{getStatusBadge(doc.status)}</td>
                          <td className="px-6 py-4 text-right">
                            {showDeleteConfirm === docId ? (
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-xs text-slate-500">Delete?</span>
                                <button
                                  onClick={() => handleDelete(docId)}
                                  disabled={deletingId === docId}
                                  className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                >
                                  {deletingId === docId ? <Loader2 size={12} className="animate-spin" /> : 'Yes'}
                                </button>
                                <button
                                  onClick={() => setShowDeleteConfirm(null)}
                                  className="px-2 py-1 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setShowDeleteConfirm(docId)}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-sm text-slate-500">
                      Page {page} of {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="px-3 py-1 text-sm rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="px-3 py-1 text-sm rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Storage Usage</h3>
            <div className="flex items-center justify-center py-6">
              <div className="relative w-40 h-40">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="80" cy="80" r="70" stroke="#f1f5f9" strokeWidth="12" fill="none" />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="#3b82f6"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray="440"
                    strokeDashoffset={strokeOffset}
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-slate-800">{usagePercent}%</span>
                  <span className="text-xs text-slate-500">Used</span>
                </div>
              </div>
            </div>
            <div className="space-y-3 mt-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Documents</span>
                <span className="font-semibold text-slate-800">{stats?.total_documents?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Chunks</span>
                <span className="font-semibold text-slate-800">{stats?.total_chunks?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Storage Used</span>
                <span className="font-semibold text-slate-800">{formatFileSize(stats?.total_storage_bytes || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Indexed</span>
                <span className="font-semibold text-slate-800">
                  {stats?.documents_by_status?.ready || stats?.documents_by_status?.indexed || 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Processing</span>
                <span className="font-semibold text-slate-800">{stats?.documents_by_status?.processing || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Upload Document</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadError(null);
                }}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* User Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Select User</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">Choose a user...</option>
                  {users.map((user: UserListItem) => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.user?.name || user.user_id} ({user.tier?.id || 'free'})
                    </option>
                  ))}
                </select>
              </div>

              {/* Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => selectedUserId && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  !selectedUserId
                    ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
                    : isUploading
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50 cursor-pointer'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.txt,.md,.doc,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={!selectedUserId || isUploading}
                />
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm text-blue-600">
                  {isUploading ? <Loader2 size={24} className="animate-spin" /> : <Upload size={24} />}
                </div>
                <h4 className="font-medium text-slate-700">
                  {isUploading
                    ? 'Uploading...'
                    : !selectedUserId
                      ? 'Select a user first'
                      : 'Drop files here or click to browse'}
                </h4>
                <p className="text-sm text-slate-500 mt-1">
                  {uploadProgress || 'Supports PDF, TXT, MD, DOC, DOCX files'}
                </p>
              </div>

              {/* Error Display */}
              {uploadError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle size={16} />
                  {uploadError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadError(null);
                }}
                className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
